import React, { useCallback } from 'react';
import { X, Package, Play, RotateCcw } from 'lucide-react';
import { Dialog } from '../../common/Dialog';
import { PlatformSelector } from './PlatformSelector';
import { BuildSettings } from './BuildSettings';
import { OptimizationOptions } from './OptimizationOptions';
import { WebSettings } from './WebSettings';
import { BuildPreview } from './BuildPreview';
import { BuildProgress } from './BuildProgress';
import { useBuildStore } from '../../../store/buildStore';
import { cn } from '../../../utils/cn';
import { showDirectoryPicker } from '../../../services/FileSystemService';

export const BuildExportDialog: React.FC = () => {
  const {
    platform,
    settings,
    isBuilding,
    progress,
    lastBuild,
    isOpen,
    activeTab,
    closeDialog,
    setPlatform,
    updateSettings,
    resetSettings,
    startBuild,
    cancelBuild,
    setActiveTab,
  } = useBuildStore();

  // Mock scenes list (would come from project store in real implementation)
  const scenes = ['MainMenu', 'Level1', 'Level2', 'GameOver'];

  const handleBrowseOutput = useCallback(async () => {
    try {
      const handle = await showDirectoryPicker();
      if (handle) {
        // Use handle.name as the path since we can't get full path from File System Access API
        updateSettings({ outputDirectory: handle.name });
      }
    } catch (error) {
      console.error('Failed to open directory dialog:', error);
    }
  }, [updateSettings]);

  const handleOpenFolder = useCallback(() => {
    if (lastBuild?.outputPath) {
      // In browser mode, we can't open a folder path directly
      // Log the path for debugging purposes
      console.log('Build output path:', lastBuild.outputPath);
    }
  }, [lastBuild]);

  const handleRunBuild = useCallback(() => {
    if (lastBuild?.outputPath) {
      // Would open the built app in real implementation
      console.log('Run build:', lastBuild.outputPath);
    }
  }, [lastBuild]);

  const canBuild = settings.startingScene && !isBuilding;

  return (
    <Dialog
      isOpen={isOpen}
      onClose={isBuilding ? () => {} : closeDialog}
      size="lg"
      closeOnEscape={!isBuilding}
      closeOnOverlayClick={!isBuilding}
    >
      <div className="flex flex-col max-h-[85vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-700">
          <div className="flex items-center gap-3">
            <Package size={24} className="text-green-400" />
            <div>
              <h2 className="text-lg font-semibold text-zinc-100">Build & Export</h2>
              <p className="text-sm text-zinc-400">Package your game for distribution</p>
            </div>
          </div>
          {!isBuilding && (
            <button
              onClick={closeDialog}
              className="p-2 hover:bg-zinc-700 rounded-lg text-zinc-400 hover:text-zinc-200"
            >
              <X size={20} />
            </button>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Platform selector */}
          <PlatformSelector
            selected={platform}
            onSelect={setPlatform}
          />

          {/* Tabs */}
          <div className="flex gap-1 border-b border-zinc-700">
            {(['settings', 'optimization', 'platform'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={cn(
                  'px-4 py-2 text-sm font-medium capitalize border-b-2 -mb-px transition-colors',
                  activeTab === tab
                    ? 'border-blue-500 text-blue-400'
                    : 'border-transparent text-zinc-400 hover:text-zinc-200'
                )}
              >
                {tab}
              </button>
            ))}
          </div>

          {/* Tab content */}
          {activeTab === 'settings' && (
            <BuildSettings
              settings={settings}
              scenes={scenes}
              onUpdate={updateSettings}
              onBrowseOutput={handleBrowseOutput}
            />
          )}

          {activeTab === 'optimization' && (
            <OptimizationOptions
              settings={settings}
              onUpdate={updateSettings}
            />
          )}

          {activeTab === 'platform' && platform === 'web' && (
            <WebSettings
              settings={settings}
              onUpdate={updateSettings}
            />
          )}

          {/* Build preview */}
          <BuildPreview
            settings={settings}
            platform={platform}
          />

          {/* Build progress */}
          <BuildProgress
            progress={progress}
            result={lastBuild}
            isBuilding={isBuilding}
            onOpenFolder={handleOpenFolder}
            onRunBuild={handleRunBuild}
          />
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-zinc-700 bg-zinc-800/50">
          <button
            onClick={resetSettings}
            disabled={isBuilding}
            className="flex items-center gap-2 px-3 py-2 text-sm text-zinc-400 hover:text-zinc-200 disabled:opacity-50"
          >
            <RotateCcw size={14} />
            Reset
          </button>

          <div className="flex gap-3">
            {isBuilding ? (
              <button
                onClick={cancelBuild}
                className="px-4 py-2 text-sm font-medium text-zinc-300 hover:text-white bg-zinc-700 hover:bg-zinc-600 rounded-lg"
              >
                Cancel
              </button>
            ) : (
              <>
                <button
                  onClick={closeDialog}
                  className="px-4 py-2 text-sm font-medium text-zinc-300 hover:text-white bg-zinc-700 hover:bg-zinc-600 rounded-lg"
                >
                  Close
                </button>
                <button
                  onClick={startBuild}
                  disabled={!canBuild}
                  className={cn(
                    'flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors',
                    canBuild
                      ? 'bg-blue-600 hover:bg-blue-500 text-white'
                      : 'bg-zinc-700 text-zinc-500 cursor-not-allowed'
                  )}
                >
                  <Play size={16} />
                  Build
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </Dialog>
  );
};
